// Project Sentinel - Auth Context
// Provides role-based access control throughout the app (roles gate every route, SEN-261).
// Calls /auth/me once on mount, caches the result.

import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react'
import { api } from '@/services/api'
import type { User } from '@/types'
import { isAdminRole, isPlatformAdminRole, meetsMinRole, type RoleCode } from '@/lib/roles'

interface AuthContextValue {
  user: User | null
  isLoading: boolean
  /** True when the user's role ranks at or above `minRole` */
  hasRole: (minRole: RoleCode) => boolean
  /** Admin of the user's own customer, or platform admin */
  isAdmin: boolean
  /** Platform admin (Sentinel operator): platform-wide settings and every customer */
  isPlatformAdmin: boolean
  /** Reload user data from server */
  refresh: () => Promise<void>
}

const AuthContext = createContext<AuthContextValue>({
  user: null,
  isLoading: true,
  hasRole: () => false,
  isAdmin: false,
  isPlatformAdmin: false,
  refresh: async () => {},
})

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  const loadUser = useCallback(async () => {
    try {
      const userData = await api.getCurrentUser()
      setUser(userData)
    } catch {
      setUser(null)
    } finally {
      setIsLoading(false)
    }
  }, [])

  useEffect(() => {
    if (api.isAuthenticated()) {
      loadUser()
    } else {
      setIsLoading(false)
    }
  }, [loadUser])

  const isAdmin = isAdminRole(user?.role)
  const isPlatformAdmin = isPlatformAdminRole(user?.role)

  const hasRole = useCallback(
    (minRole: RoleCode) => meetsMinRole(user?.hierarchy_level, minRole),
    [user]
  )

  return (
    <AuthContext.Provider
      value={{
        user,
        isLoading,
        hasRole,
        isAdmin,
        isPlatformAdmin,
        refresh: loadUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  return useContext(AuthContext)
}
